import { Component, EventEmitter, Input, Output } from "@angular/core"
import { IWriter, ICustomer } from "graphql/queries/keyword.queries"

@Component({
  selector: 'writers-list',
  template: `
    <ng-container *ngIf="( preferredWritersList | filterPreferredWriters : preferredWriterSearch ) as result;">
      <hr *ngIf="section === 'all clients'">
      <h6 class="mt-2">
        {{section}}
      </h6>
      <div *ngFor="let writer of result; index as i">
        <button
          class="writer-list-item btn-blank btn-block"
          [class.item-active]="isWriterPreAssigned(writer)"
          [class.text-disabled]="canNotAssignToWriter(writer)"
          [disabled]="canNotAssignToWriter(writer)"
          (click)="onPreAssignWriter(writer)">
          <writer-avatar
            class="tiny-left-buffer tiny-right-buffer d-inline-block"
            [writer]="writer"
            [customerId]="writer.id"
            [size]="30"
            [hideName]="true"
            [linkToProfile]="false"
            borderRadius="50%"
            tooltip="Assign to {{writer.displayName}}"
            containerClass="white-tooltip"
            triggers="mouseenter:mouseleave"
            [placement]="tooltipPlacement(i)"
            >
          </writer-avatar>
          <div>{{ writer.displayName }}</div>
          <i
            *ngIf="writer.userFraudCount > 0"
            class="ml-2 fa-solid fa-mask fa-xs text-red"
            popover="Tagged for fraud. Proceed with caution."
            triggers="mouseover:mouseout"
          ></i>
          <red-dot
            [show]="showWriterStatus(writer)"
            [dotColor]="getStatusColor(writer)"
            class="ml-2"
            [tooltipText]="getStatusTooltip(writer)"
            [placement]="tooltipPlacement(i)">
          </red-dot>
        </button>
      </div>
      <small *ngIf="result.length === 0" class="text-muted">
        Hmm... no matching writers
      </small>
    </ng-container>
  `
})
export class WritersListComponent {
  @Input() preferredWritersList: IWriter[] = []
  @Input() customer: ICustomer = null
  @Input() writerPreAssigned: boolean
  @Input() preAssignedWriter: IWriter
  @Input() section: string
  @Input() preferredWriterSearch: string
  @Output() writerPreAssignedChange = new EventEmitter<boolean>()
  @Output() preAssignedWriterChange = new EventEmitter<IWriter>()

  isWriterPreAssigned(writer: IWriter): boolean {
    if (this.preAssignedWriter) {
      return this.preAssignedWriter.id === writer.id
    }
    return false
  }

  onPreAssignWriter(writer: IWriter): void {
    this.preAssignedWriterChange.emit(writer)
    this.writerPreAssignedChange.emit(true)
  }

  tooltipPlacement(index: number): 'top' | 'bottom' {
    return index === 0 ? 'bottom' : 'top'
  }

  getStatusTooltip(writer: IWriter): string {
    if (this.isBlockedWriter(writer)) { return 'This writer is blocked' }
    if (this.isWriterUnavailable(writer)) { return 'Writer is unavailable' }
    if (this.isPooledWriter(writer) && !this.isPreferredWriter(writer)) { return 'Pooled writers are only visible in this list if you’re a Verblio admin' }
  }

  showWriterStatus(writer: IWriter): boolean {
    return this.isWriterUnavailable(writer) || (this.isPooledWriter(writer) && !this.isPreferredWriter(writer))
  }

  isWriterUnavailable(writer: IWriter): boolean {
    return writer.state === 'unavailable'
  }

  canNotAssignToWriter(writer: IWriter): boolean {
    return this.isWriterUnavailable(writer) || this.isBlockedWriter(writer)
  }

  isPooledWriter(writer: IWriter): boolean {
    return this.customer.pooledWriters.some(w => w === writer)
  }

  isPreferredWriter(writer: IWriter): boolean {
    return this.customer.preferredWriters.some(w => w === writer)
  }

  isBlockedWriter(writer: IWriter): boolean {
    return this.customer.blockedWriters.some(w => w.id === writer.id)
  }

  getStatusColor(writer: IWriter): string {
    if (this.isWriterUnavailable(writer) || this.isBlockedWriter(writer)) { return 'text-red' }
    if (this.isPooledWriter(writer) && !this.isPreferredWriter(writer)) { return 'text-secondary' }
  }
}
